import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // FCD: an empty cart used to wait for /cart/add.js + the cart refresh (~1-1.5s) before
    // opening, which read as a stuck button. Open right away with an "Adding to cart…"
    // state over the empty-cart markup; cart-items-component fills it when the add lands.
    const openEarly = shouldAutoOpen && !sourceModal && this.#isCartEmpty();

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    } else if (openEarly) {
      this.#setAdding(true);
      this.#themeDrawer?.open();
    }
    const addingRun = this.#addingRun;

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (openEarly) {
          // A stock-limited add still puts what's available in the cart (Shopify returns 422
          // "All 1 … are in your cart"), so only slide back out when the cart is still empty.
          if (detail?.didError && !detail?.items?.length) {
            this.#closeAfterFailedAdd(addingRun);
          } else {
            this.#clearAddingWhenFilled(addingRun);
          }
          // Already open; if the shopper closed it while waiting, don't pop it back up.
          settle();
          return;
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (openEarly) this.#closeAfterFailedAdd(addingRun);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Bumped every time the loading state is shown, so a slow cleanup from an earlier add
   * (e.g. a failed add's close animation finishing) can't hide the spinner of a newer one.
   * @type {number}
   */
  #addingRun = 0;

  /** @param {boolean} adding */
  #setAdding(adding) {
    if (adding) this.#addingRun += 1;
    this.toggleAttribute('data-adding', adding);
    const status = this.querySelector('[data-cart-drawer-adding]');
    if (status instanceof HTMLElement) status.hidden = !adding;
  }

  /** @param {number} run */
  #clearAdding(run) {
    if (run === this.#addingRun) this.#setAdding(false);
  }

  /**
   * The add failed (e.g. sold out), so there's nothing to show — slide the drawer back out.
   * The loading state stays up until the dialog has finished closing, otherwise
   * "Your cart is empty" shows during the slide-out.
   * @param {number} run
   */
  #closeAfterFailedAdd(run) {
    const dialog = this.#dialog;
    const clear = () => this.#clearAdding(run);

    if (dialog?.open) {
      dialog.addEventListener('close', clear, { once: true });
      setTimeout(clear, 1000);
    } else {
      clear();
    }
    this.#themeDrawer?.close();
  }

  /**
   * The empty → filled morph runs in a view transition off the same promise, so it can land a
   * frame or two after this resolves. Keep the loading state until the empty markup is gone so
   * "Your cart is empty" never flashes; the timeout covers a morph that never comes.
   * @param {number} run
   */
  #clearAddingWhenFilled(run) {
    if (!this.querySelector('[data-cart-drawer-empty]')) {
      this.#clearAdding(run);
      return;
    }

    const observer = new MutationObserver(() => {
      if (this.querySelector('[data-cart-drawer-empty]')) return;
      observer.disconnect();
      clearTimeout(timeout);
      this.#clearAdding(run);
    });
    const timeout = setTimeout(() => {
      observer.disconnect();
      this.#clearAdding(run);
    }, 2000);
    observer.observe(this, { childList: true, subtree: true });
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
